import { trackEnquiryClick, trackGaEvent } from '@propertypal/shared/src/services/analytics';
import { setItem } from '@propertypal/shared/src/services/local.storage';
import { getGalleryImages } from '@propertypal/shared/src/utils/property';
import requiresEligibilityConfirmation from '@propertypal/shared/src/utils/property/eligibilityCriteria';
import getPropertyTour from '@propertypal/shared/src/utils/property/getPropertyTour';
import getPropertyVideos from '@propertypal/shared/src/utils/property/getPropertyVideos';
import { ALL_PHASES_VALUE } from '@propertypal/shared/src/utils/property/propertyPhase';
import BroadbandTable from '@propertypal/web-ui/src/broadband/BroadbandTable';
import WhiteButton from '@propertypal/web-ui/src/buttons/WhiteButton';
import CoOwnershipJumbo from '@propertypal/web-ui/src/jumbotrons/CoOwnershipJumbo';
import PageWrapper from '@propertypal/web-ui/src/layout/PageWrapper';
import { MAX_WIDTH } from '@propertypal/web-ui/src/layout/PageWrapper.style';
import ImageModal from '@propertypal/web-ui/src/modal/ImageModal';
import WhiteModal from '@propertypal/web-ui/src/modal/WhiteModal';
import AgentAccreditations from '@propertypal/web-ui/src/property/AgentAccreditations';
import ContactTable from '@propertypal/web-ui/src/property/ContactTable';
import CurrencyDirectAd from '@propertypal/web-ui/src/property/CurrencyDirectAd';
import DevelopmentHeader from '@propertypal/web-ui/src/property/DevelopmentHeader';
import KeyInfo from '@propertypal/web-ui/src/property/KeyInfo';
import OtherDevelopments from '@propertypal/web-ui/src/property/OtherDevelopments';
import PromotePropertyStats from '@propertypal/web-ui/src/property/PromotePropertyStats';
import PropertyAgentBar from '@propertypal/web-ui/src/property/PropertyAgentBar';
import PropertyAgentBox from '@propertypal/web-ui/src/property/PropertyAgentBox';
import PropertyAttributes from '@propertypal/web-ui/src/property/PropertyAttributes';
import PropertyDescription from '@propertypal/web-ui/src/property/PropertyDescription';
import { TextBox } from '@propertypal/web-ui/src/property/PropertyDescription.style';
import PropertyInfoBar from '@propertypal/web-ui/src/property/PropertyInfoBar';
import PropertyLocation from '@propertypal/web-ui/src/property/PropertyLocation';
import PropertyRooms from '@propertypal/web-ui/src/property/PropertyRooms';
import PropertySummary from '@propertypal/web-ui/src/property/PropertySummary';
import PropertyThumbTiles from '@propertypal/web-ui/src/property/PropertyThumbTiles';
import PropertyUpgrade from '@propertypal/web-ui/src/property/PropertyUpgrade';
import RelatedLinks from '@propertypal/web-ui/src/property/RelatedLinks';
import ShowHomesBar from '@propertypal/web-ui/src/property/ShowHomesBar';
import SimilarProperties from '@propertypal/web-ui/src/property/SimilarProperties';
import SlideshowBar from '@propertypal/web-ui/src/property/SlideshowBar';
import Breadcrumbs from '@propertypal/web-ui/src/search/Breadcrumbs';
import { Bold, SubHeading, Text } from '@propertypal/web-ui/src/typography';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import React, { useState } from 'react';
import { LEGAL_FEES_RESULT_STORAGE_KEY } from '../../hooks/useCachedLegalFees';
import AdBox from '../ads/AdBox';
import GoogleMap from '../map/GoogleMap';
import BrokerModal from './BrokerModal';
import { PROPERTY_IMAGE_LOCATOR, PropertyDetailsContentProps, routerUpdate } from './PropertyDetails';
import { Content, LeftPane, RightPane, StickyAdWrapper, TopSection } from './PropertyDetailsContent.style';
import PropertyTypesCarousel from './PropertyTypesCarousel';
import PropertyUnits from './PropertyUnits';

const AD_BOX_2_SIZES = [[728, 90], 'fluid'];
const AD_BOX_3_SIZES = [[320, 100], [300, 250], [336, 280], 'fluid'];
const AD_BOX_4_SIZES = [
  [300, 250],
  [300, 600],
  [160, 600],
];
const AD_BOX_SPONSOR_SIZES = [[1, 1], 'fluid'];

const BrokerCalculator = dynamic(() => import('@propertypal/web-ui/src/property/BrokerCalculator'), { ssr: false });
const LegalFeesCalculator = dynamic(() => import('@propertypal/web-ui/src/property/LegalFeesCalculator'), {
  ssr: false,
});
const PropertyStats = dynamic(() => import('@propertypal/web-ui/src/property/PropertyStats'), { ssr: false });
const SchoolsModal = dynamic(() => import('../schools/SchoolsModal'), { ssr: false });
const EnquiryModal = dynamic(() => import('./EnquiryModal'), { ssr: false });
const EligibilityConfirmationModal = dynamic(() => import('./EligibilityConfirmationModal'), { ssr: false });
const LegalFeesModal = dynamic(() => import('./LegalFeesModal'), { ssr: false });
const PropertySlideshowModal = dynamic(() => import('./PropertySlideshowModal'), { ssr: false });
const ReportProblemModal = dynamic(() => import('./ReportProblemModal'), { ssr: false });
const ShareModal = dynamic(() => import('./ShareModal'), { ssr: false });
const StampDutyModal = dynamic(() => import('./StampDutyModal'), { ssr: false });
const NotesModal = dynamic(() => import('./NotesModal'), { ssr: false });
const MarketTrendsModal = dynamic(() => import('./MarketTrendsModal'), { ssr: false });
const PropertyTypesModal = dynamic(() => import('./PropertyTypesModal'), { ssr: false });

const PropertyDetailsContent: React.FC<PropertyDetailsContentProps> = ({
  property,
  loggedIn,
  adSuffix,
  updateSectionUrl,
  imageIndex,
  updatePhotoUrl,
  showSlideshowModal,
  getReplaceUrl,
  hasSitemap,
  hasFeaturedBrochure,
  favourited,
  toggleFavourited,
  leadId,
  handleInfoClick,
  buyingStatus,
  callAgent,
  modal,
  showPhoneModal,
  ukResident,
  mortgageCalculator,
  onFavourite,
  handleShare,
  isFeaturedDevelopment,
  similarProperties,
  fetchSimilarProperties,
  showLegalFees,
  hasLegalContact,
  setEnquiryAccountNumber,
  showSchoolsModal,
  showMarketTrends,
  showPromoteText,
  showDescription,
  marketTrendsData,
  marketTrendsError,
  marketTrendsLoading,
  fetchMarketTrendsData,
  otherDevelopments,
  fetchOtherDevelopments,
  modalImage,
  setModalImage,
  showStampDutyModal,
  setBuyingStatus,
  setUkResident,
  handleBack,
  closeModal,
  showEnquiryModal,
  showRegisterInterestModal,
  showStatsModal,
  enquiryAccountNumber,
  setLeadId,
  showLegalFeesModal,
  showReportModal,
  showShareModal,
  isAdObscuringModalOpen,
  showNotesModal,
  showMapModal,
  notes,
  setNotes,
  showBrokerModal,
  showPropertyTypesModal,
}) => {
  const router = useRouter();
  const images = getGalleryImages(property, 'hd');
  const [phase, setPhase] = useState<string>(ALL_PHASES_VALUE);
  const [showEligibilityConfirmModal, setShowEligibilityConfirmModal] = useState(false);
  const [pendingEligibilityAction, setPendingEligibilityAction] = useState<(() => void) | undefined>();

  const closeEligibilityModal = () => {
    setShowEligibilityConfirmModal(false);
    setPendingEligibilityAction(undefined);
  };

  const withEligibilityConfirmation = (action: () => void) => {
    if (!requiresEligibilityConfirmation(property)) {
      action();
      return;
    }

    setPendingEligibilityAction(() => action);
    setShowEligibilityConfirmModal(true);
  };

  const confirmEligibility = () => {
    const action = pendingEligibilityAction;

    closeEligibilityModal();
    if (action) action();
  };

  const agentBox = (
    <PropertyAgentBox
      property={property}
      onCallClick={(location) =>
        withEligibilityConfirmation(() =>
          callAgent(property, undefined, undefined, property.type === 'development' ? 'DDP' : 'PDP', location),
        )
      }
      onShowNumberClick={(location, accountNumber) => {
        withEligibilityConfirmation(() => {
          showPhoneModal(
            property,
            undefined,
            undefined,
            property.type === 'development' ? 'DDP' : 'PDP',
            location,
            undefined,
            accountNumber,
          );
          updateSectionUrl('/call-the-agent');
        });
      }}
      onEnquiryClick={(accountNumber) => {
        withEligibilityConfirmation(() => {
          setEnquiryAccountNumber(accountNumber || '');
          updateSectionUrl('/enquiry');
        });
      }}
      onRegisterInterestClick={() => updateSectionUrl('/register-interest')}
      registerInterest={property.registerInterest}
      leadId={leadId}
      mortgageCalculator={mortgageCalculator}
    />
  );

  return (
    <div>
      <PageWrapper>
        <Breadcrumbs crumbs={property.breadcrumbs} padding={0} />
      </PageWrapper>

      <PropertyAgentBar property={property} hideOnMobile />

      {property.showHomeOpeningTime && <ShowHomesBar property={property} />}

      {property.featuredDevelopmentContent?.contentTop && (
        <DevelopmentHeader
          html={property.featuredDevelopmentContent?.contentTop}
          property={property}
          onOpenSlideshowClick={() => updateSectionUrl('/slideshow', 0)}
          onImageClick={(index) => updateSectionUrl('/slideshow', index)}
        />
      )}

      {!property.featuredDevelopmentContent?.contentTop && (
        <PropertyThumbTiles
          property={property}
          onClick={(index, type) => {
            const propertyVideo = getPropertyVideos(property);
            const propertyTour = getPropertyTour(property);
            const page = property.type === 'development' ? 'DDP' : 'PDP';
            let button = '';

            if (type === 'VIDEO' && propertyVideo) {
              button = 'Video';
              const videoPath = propertyVideo.tourIndex !== undefined ? `tour${propertyVideo.tourIndex + 1}` : 'video';
              router.push(`${property.path}/${videoPath}`, undefined, { shallow: true });
            } else if (type === 'TOUR' && propertyTour) {
              button = 'Tour';
              if (propertyTour.insecure) {
                window.open(propertyTour.url, '_blank', 'noopener,noreferrer');
              } else {
                const tourPath = propertyTour.tourIndex !== undefined ? `tour${propertyTour.tourIndex + 1}` : '';
                if (tourPath) router.push(`${property.path}/${tourPath}`, undefined, { shallow: true });
              }
            } else {
              button = 'Slideshow';
              router.push(`${property.path}/slideshow${index === 0 ? '' : `/photo-${index + 1}`}`, undefined, {
                shallow: true,
              });
            }

            trackGaEvent('media_box_click', {
              button,
              page,
              location: page,
            });
          }}
        />
      )}

      {property.coordinate &&
        !showMapModal &&
        images.length === 0 &&
        !property.featuredDevelopmentContent?.contentTop && (
          <GoogleMap
            width="100%"
            height={300}
            lat={property.coordinate.latitude}
            lng={property.coordinate.longitude}
            zoom={property.coordinate.mapZoomLevel}
            markers={[
              {
                id: 1,
                lat: property.coordinate.latitude,
                lng: property.coordinate.longitude,
              },
            ]}
          />
        )}

      <SlideshowBar
        idPrefix="page-bar"
        getReplaceUrl={getReplaceUrl}
        property={property}
        hasSitemap={hasSitemap}
        hasFeaturedBrochure={hasFeaturedBrochure}
        replace={false}
        maxWidth={MAX_WIDTH}
        location="PDP"
      />

      <TopSection className="pp-background-color">
        <div>
          <AdBox
            id="PDPsponsor_desktop"
            config="PDPsponsor_desktop"
            sizes={AD_BOX_SPONSOR_SIZES}
            hideUnder={970}
            renderedStyle={{ margin: '0 auto', paddingTop: 15, maxWidth: MAX_WIDTH - 60 }}
            collapse
          />
          <AdBox
            id="PDPsponsor_mobile"
            config="PDPsponsor_mobile"
            sizes={AD_BOX_SPONSOR_SIZES}
            hideOver={969}
            renderedStyle={{ margin: '0 auto', paddingTop: 15 }}
            collapse
          />

          <Content>
            <LeftPane>
              <PropertySummary
                property={property}
                favourited={favourited}
                toggleFavourited={toggleFavourited}
                onShare={handleShare}
                loggedIn={loggedIn}
                notes={notes}
              />

              <PropertyInfoBar property={property} onClick={handleInfoClick} />

              {agentBox}

              <KeyInfo
                property={property}
                onClick={handleInfoClick}
                buyingStatus={buyingStatus}
                ukResident={ukResident}
                mortgageCalculator={mortgageCalculator}
                showLegalFees={showLegalFees}
              />

              <PropertyAttributes property={property} />

              <CurrencyDirectAd property={property} />

              {property.units && (
                <PropertyUnits
                  property={property}
                  hasSitemap={hasSitemap}
                  onSiteMapClick={(unit) => updateSectionUrl(`/sitemap/${unit.name}`)}
                />
              )}

              <PropertyDescription property={property} />

              {!property.featuredDevelopmentContent && (
                <AdBox
                  id="PDPmiddle_desktop"
                  config="PDPmiddle_desktop"
                  sizes={AD_BOX_2_SIZES}
                  hideUnder={779}
                  obscured={isAdObscuringModalOpen}
                  containerStyle={{
                    marginTop: 25,
                    marginBottom: 45,
                    paddingTop: 35,
                    paddingBottom: 35,
                    background: '#f5f5f5',
                  }}
                  displayMargin={300}
                  declaration
                  refresh
                  timedRefresh
                />
              )}

              {!property.featuredDevelopmentContent && (
                <AdBox
                  id="PDPmiddle_mobile"
                  config="PDPmiddle_mobile"
                  sizes={AD_BOX_3_SIZES}
                  hideOver={780}
                  obscured={isAdObscuringModalOpen}
                  containerStyle={{
                    marginTop: 25,
                    marginBottom: 45,
                    paddingTop: 35,
                    paddingBottom: 35,
                    background: '#f5f5f5',
                  }}
                  displayMargin={300}
                  declaration
                  refresh
                  timedRefresh
                />
              )}

              {property.type === 'property' && (
                <PropertyLocation
                  property={property}
                  updateSectionUrl={updateSectionUrl}
                  fetchMarketTrendsData={fetchMarketTrendsData}
                  marketTrendsData={marketTrendsData}
                  marketTrendsLoading={marketTrendsLoading}
                />
              )}

              {mortgageCalculator.hasMortgageCalculator && (
                <>
                  <div id="calculator-marker" />
                  <BrokerCalculator property={property} mortgageCalculator={mortgageCalculator} />
                </>
              )}

              {showLegalFees && (
                <>
                  <div id="legal-fees-marker" />
                  <LegalFeesCalculator
                    hasContact={hasLegalContact}
                    onCalculateClick={() => {
                      trackGaEvent('legal_fees_click', { page: 'PDP', location: 'Legal Fees Ad' });
                      updateSectionUrl('/legal-fees');
                    }}
                    property={property}
                  />
                </>
              )}

              <div id="broadband-table" />

              <BroadbandTable property={property} />

              {showPromoteText && <PromotePropertyStats property={property} />}

              {(!!property.account?.accreditations?.length || !!property.account.missingAccreditations?.length) &&
                property.type === 'property' &&
                !property.featuredDevelopmentContent && (
                  <>
                    <SubHeading mt={60}>Agent Accreditations</SubHeading>

                    <AgentAccreditations
                      accreditations={property.account.accreditations}
                      missingAccreditations={property.account.missingAccreditations}
                    />
                  </>
                )}

              {property.type === 'property' && (
                <>
                  <SubHeading mt={30}>Report a Problem</SubHeading>
                  <Text mt={15} mb={showPromoteText ? 0 : 30}>
                    <Bold>Spotted a problem?</Bold> Even the best agents make mistakes, help us to maintain accurate
                    property information on our listings.
                  </Text>
                  <WhiteButton
                    containerStyle={{ maxWidth: 220, height: 45, marginTop: 15 }}
                    onClick={() => updateSectionUrl('/report-a-problem')}
                    testID="report-problem-btn"
                  >
                    Report a problem
                  </WhiteButton>
                </>
              )}
            </LeftPane>

            <RightPane>
              {agentBox}

              {showPromoteText && <PropertyUpgrade property={property} />}

              {!property.featuredDevelopmentContent && (
                <StickyAdWrapper>
                  <AdBox
                    id="PDPright_desktop"
                    config="PDPright_desktop"
                    sizes={AD_BOX_4_SIZES}
                    obscured={isAdObscuringModalOpen}
                    refresh
                    timedRefresh
                    collapse
                  />
                </StickyAdWrapper>
              )}
            </RightPane>
          </Content>
        </div>

        {!!property.properties?.length && property.published && (
          <>
            <PropertyTypesCarousel
              properties={property.properties}
              phase={phase}
              setPhase={setPhase}
              onFavourite={onFavourite}
              onViewAll={() => updateSectionUrl('/property-types')}
            />
          </>
        )}
      </TopSection>

      {property.type === 'property' && property.development && isFeaturedDevelopment && (
        <SimilarProperties
          properties={similarProperties}
          onInView={fetchSimilarProperties}
          title="More from this development"
          background="transparent"
          hideOnEmpty
          onFavourite={onFavourite}
          showArrows
        />
      )}

      {/* {shouldDisplayGuides(property) && (
          <Accordion
            className="hide-print"
            isOpen={!isMobile || openSections.includes('contentHubGuides')}
            header={renderAccordionHeader('contentHubGuides', 'Home Buying Guides')}
          >
            <ContentHubGuidesPreview />
          </Accordion>
        )} */}

      {property.type === 'property' && !property.development && (
        <SimilarProperties
          properties={similarProperties}
          onInView={fetchSimilarProperties}
          title="Similar Properties"
          background="transparent"
          onFavourite={onFavourite}
          emptyText="Sorry, we couldn't find any similar properties to this"
        />
      )}

      {property.featuredDevelopmentContent && (
        <div
          className="pp-development-content"
          dangerouslySetInnerHTML={{ __html: property.featuredDevelopmentContent.contentMiddle }}
        />
      )}

      {property.type === 'development' && (
        <OtherDevelopments data={otherDevelopments} onInView={fetchOtherDevelopments} />
      )}

      {property.featuredDevelopmentContent && (
        <div
          className="pp-development-content"
          dangerouslySetInnerHTML={{ __html: property.featuredDevelopmentContent.contentBottom }}
        />
      )}

      {(property.type === 'development' || property.development) &&
        property.contacts &&
        property.contacts.all.length > 1 &&
        !property.featuredDevelopmentContent?.hideAgentContact &&
        property.published && (
          <ContactTable
            property={property}
            onEnquiryClick={(contact) => {
              trackEnquiryClick('DDP', 'Contact Table', property, contact.accountNumber);
              setEnquiryAccountNumber(contact.accountNumber);
              updateSectionUrl('/enquiry');
            }}
            onRegisterInterestClick={(contact) => {
              setEnquiryAccountNumber(contact.accountNumber);
              updateSectionUrl('/register-interest');
            }}
            registerInterest={property.registerInterest}
          />
        )}

      <RelatedLinks property={property} />

      <CoOwnershipJumbo property={property} />

      <ImageModal
        title="Energy Performance Certificate"
        ariaLabel="EPC image popup"
        show={!!modalImage}
        onClose={() => setModalImage('')}
        imageUrl={modalImage}
      />

      {showDescription && property.description && (
        <WhiteModal show={showDescription} onClose={closeModal} title="Additional Information">
          <TextBox style={{ marginTop: -15 }} dangerouslySetInnerHTML={{ __html: property.description }} />
          {!!property?.rooms?.length && <PropertyRooms property={property} />}
        </WhiteModal>
      )}

      {showStampDutyModal && (
        <StampDutyModal
          buyingStatus={buyingStatus}
          setBuyingStatus={setBuyingStatus}
          ukResident={ukResident}
          setUkResident={setUkResident}
          property={property}
          show={showStampDutyModal}
          onClose={closeModal}
        />
      )}

      {showSlideshowModal && (
        <PropertySlideshowModal
          imageIndex={imageIndex}
          setImageIndex={updatePhotoUrl}
          show={showSlideshowModal}
          property={property}
          onClose={handleBack}
          // onTabClick={(tab, index) => updateSectionUrl(`/${tab}`, index)}
          url={router.asPath}
          imageLocator={PROPERTY_IMAGE_LOCATOR[0]}
          hasSitemap={hasSitemap}
          hasFeaturedBrochure={hasFeaturedBrochure}
          getReplaceUrl={getReplaceUrl}
          updateSectionUrl={updateSectionUrl}
          adSuffix={adSuffix}
          onEnquiryClick={() => updateSectionUrl('/enquiry')}
          onCallClick={(location) =>
            showPhoneModal(property, undefined, undefined, property.type === 'development' ? 'DDP' : 'PDP', location)
          }
        />
      )}

      {showEligibilityConfirmModal && (
        <EligibilityConfirmationModal
          show={showEligibilityConfirmModal}
          onClose={closeEligibilityModal}
          onConfirm={confirmEligibility}
        />
      )}

      {(showEnquiryModal || showRegisterInterestModal) && (
        <EnquiryModal
          accountNumber={enquiryAccountNumber}
          show={showEnquiryModal || showRegisterInterestModal}
          onClose={closeModal}
          property={property}
          registerInterestModal={showRegisterInterestModal}
          onSuccess={setLeadId}
          loggedIn={loggedIn}
          loggedInUser={property.loggedInUser}
          page={property.type === 'development' ? 'DDP' : 'PDP'}
        />
      )}

      {showLegalFeesModal && (
        <LegalFeesModal
          show={showLegalFeesModal}
          onClose={() => {
            // clear result from local storage
            setItem(LEGAL_FEES_RESULT_STORAGE_KEY, null);
            const nextUrl = getReplaceUrl('');
            const [baseUrl, qs] = nextUrl.split('?');

            let url = baseUrl;

            // remove result querystring
            if (qs) {
              const params = new URLSearchParams(qs);
              params.delete('result');

              if (params.toString()) {
                url = `${baseUrl}?${params.toString()}`;
              }
            }

            routerUpdate(router, url);
          }}
          property={property}
          buyingStatus={buyingStatus}
          setBuyingStatus={setBuyingStatus}
          ukResident={ukResident}
          setUkResident={setUkResident}
          source="Legal Calc Form"
        />
      )}

      {showReportModal && <ReportProblemModal show={showReportModal} onClose={closeModal} property={property} />}

      {showShareModal && (
        <ShareModal show={router.asPath.includes('/share')} onClose={closeModal} property={property} />
      )}

      {showSchoolsModal && <SchoolsModal property={property} show={showSchoolsModal} onClose={closeModal} />}

      {showStatsModal && (
        <WhiteModal title="Property Statistics" maxWidth={MAX_WIDTH} show={showStatsModal} onClose={closeModal}>
          <PropertyStats property={property} showPromoteText={showPromoteText} />
        </WhiteModal>
      )}

      {showMarketTrends && (
        <MarketTrendsModal
          addressLine1={property.addressLine1}
          marketTrendsData={marketTrendsData}
          error={marketTrendsError}
          loading={marketTrendsLoading}
          onClose={closeModal}
          show={showMarketTrends}
        />
      )}

      {showNotesModal && (
        <NotesModal
          show={showNotesModal}
          onClose={closeModal}
          property={property}
          notes={notes}
          onNoteUpdate={(n) => {
            setNotes(n);
            closeModal();
          }}
        />
      )}

      {showBrokerModal && (
        <BrokerModal
          show={showBrokerModal}
          onClose={closeModal}
          property={property}
          mortgageCalculator={mortgageCalculator}
        />
      )}

      {showPropertyTypesModal && property.properties && (
        <PropertyTypesModal
          show={showPropertyTypesModal}
          onClose={closeModal}
          properties={property.properties}
          phase={phase}
          setPhase={setPhase}
          onFavourite={onFavourite}
        />
      )}

      {modal}

      {property.featuredDevelopmentContent?.css && (
        <>
          <style>
            {`
              .pp-info-box { border-color: #dddddd; }
              .pp-info-box-header { background-color: #eeeeee; }
              .pp-btn { background-color: #353535; }
              .pp-btn:hover { background-color: #4a4a4a; }
              .pp-primary-text { color: #353535; }
              .pp-school-table-title svg { color: #353535; } 
              .pp-school-table-row span { border-color: #d8d8d8; }
              .pp-multi-select-pill { background-color: #353535; }
              .pp-validation-error { background-color: #d0021b; } 
              .pp-input-error { box-shadow: inset 0 0 0 2px #d0021b !important; }
              .pp-development-content h1,
              .pp-development-content h2,
              .pp-development-content h3,
              .pp-development-content h4,
              .pp-development-content h5,
              .pp-development-content h6,
              .pp-development-content p {
                margin: revert;
                line-height: revert;
              }
            `}
          </style>
          <style dangerouslySetInnerHTML={{ __html: property.featuredDevelopmentContent.css }} />
        </>
      )}
    </div>
  );
};

export default PropertyDetailsContent;
